/**
 * Copyright (c) 2017 CRF Box Oy. All Rights Reserved. Company Confidential.
 */

import { connect } from 'react-redux';
import Documents from './documents.component';
import {
  fetchConsentingJournal,
  publishConsentGidandConsentingGidForJournal,
  fetchDocuments,
  publishConsentDocumentsForParticipant,
  fetchConsentDocumentForParticipant,
  fetchConsentPdf,
  changeTab,
  showModal,
  closeDocumentPdf,
  uploadPdf,
  errorFetchingDocuments,
  fetchAvailableDocuments,
  clearDocuments,
  fetchConsenting,
  fetchConsentMetadata
} from '../../actions/actions';
import {
  MODAL_TYPE_EDIT_DOCUMENT_STATUS,
  MODAL_TYPE_ADD_NOTE,
  MODAL_TYPE_PDF_MODAL,
  MODAL_TYPE_DELETE_PDF,
  MODAL_TYPE_ADD_NEW_PARTICIPANT_DOCUMENT,
  MODAL_TYPE_DELETE_ADDENDUM,
  MODAL_TYPE_CHANGE_ANSWERS
} from '../../constants/modalTypes';
import DateHelper from '../../helper/date/index';
import {
  VERSION,
  ADD_NOTE,
  CHANGE_DOCUMENT_STATUS,
  ADD_NEW_PARTICIPANT_DOCUMENT_TITLE,
  DELETE_ADDENDUM_MODAL_TITLE,
  CHANGE_ANSWERS
} from '../../const';
import DocumentsService from './documents.service';
import SubjectService from '../subject/subject.service';
import RequestService from '../../service/requestService';

const orderAddendumsByDateAndNameDescInDocuments = (documents) => {
  const comparareAddendumsByDateAndNameDesc = (addendum1, addendum2) => {
    const dateCompare = new Date(addendum2.createdOn).getTime() - new Date(addendum1.createdOn).getTime();
    return dateCompare !== 0 ? dateCompare : parseInt(addendum2.name) - parseInt(addendum1.name);
  };

  const mapDocumentByOrderingItsAddendums = (document) => {
    const { addendums, ...rest } = document;
    if (!addendums || addendums.length === 0) {
      return { ...document };
    }

    const listOfAddendumOrdered = [...addendums];
    listOfAddendumOrdered.sort(comparareAddendumsByDateAndNameDesc);

    return { ...rest, addendums: listOfAddendumOrdered };
  };

  return documents.map(mapDocumentByOrderingItsAddendums);
};

export const mapStateToProps = (store) => {
  let [timezone] = SubjectService.getSiteTimezone(store.subject.participantSites);
  return {
    studyGid: store.documents.studyGid,
    participant: store.subject.participantJourney,
    isFetching: store.documents.isFetching,
    documents: store.documents.items && orderAddendumsByDateAndNameDescInDocuments(store.documents.items),
    error: store.documents.error,
    reporter: store.login.user,
    timezone
  };
};

export const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    fetchDocuments: (studyGid, participantId) => {
      dispatch(fetchDocuments({ studyGid, participantId }));
    },
    clearDocuments: () => dispatch(clearDocuments()),
    createNewNote: (consentGid, consentingGid, user) => {
      dispatch(publishConsentDocumentsForParticipant(consentGid, consentingGid));
      dispatch(fetchConsentDocumentForParticipant(consentGid));
      dispatch(
        showModal(MODAL_TYPE_ADD_NOTE, {
          shouldCloseOnOverlayClick: false,
          title: ADD_NOTE,
          details: DateHelper.timestampToHumanReadableString(new Date()) + ', ' + user.firstName + ' ' + user.lastName,
          className: 'add-note-modal',
          props: { consentGid, consentingGid }
        })
      );
    },
    editDocumentStatus: (consentGid, consentingGid, consentName, currentStatus, consentVersion, signedTimestamp) => {
      dispatch(
        showModal(MODAL_TYPE_EDIT_DOCUMENT_STATUS, {
          shouldCloseOnOverlayClick: false,
          title: CHANGE_DOCUMENT_STATUS,
          details: [consentName, '-', VERSION, consentVersion].join(' '),
          className: 'document-status-modal',
          props: { consentGid, consentingGid, consentName, currentStatus, consentVersion, signedTimestamp }
        })
      );
    },
    changeAnswers: (consenting = {}) => {
      const {
        consentGid,
        consentingGid,
        consentName,
        documentVersion: consentVersion,
        signedTimestamp,
        addendums
      } = consenting;

      let minSignatureDate = addendums && addendums.length > 0 && addendums[0].createdOn;
      minSignatureDate = minSignatureDate || signedTimestamp;

      dispatch(
        showModal(MODAL_TYPE_CHANGE_ANSWERS, {
          shouldCloseOnOverlayClick: false,
          title: CHANGE_ANSWERS,
          details: [consentName, '-', VERSION, consentVersion].join(' '),
          className: 'change-answers-date-reason-modal',
          props: { consentGid, consentingGid, consentName, consentVersion, minSignatureDate }
        })
      );
    },
    fetchPdf: (consentId, consentingId, isSigned, documentName, addendumId) => {
      dispatch(fetchConsentPdf(consentId, consentingId, isSigned, addendumId));
      dispatch(
        showModal(MODAL_TYPE_PDF_MODAL, {
          shouldCloseOnOverlayClick: false,
          className: 'pdf-modal',
          documentName: documentName
        })
      );
    },
    generatePdf: async (consentingGid, studyGid, userId) => {
      await dispatch(fetchConsentMetadata(consentingGid, studyGid, userId));
    },
    deleteAddendum:
      ({ studyGid, participantId }) =>
      (addendum) => {
        dispatch(
          showModal(MODAL_TYPE_DELETE_ADDENDUM, {
            shouldCloseOnOverlayClick: false,
            className: 'delete-document-modal',
            title: DELETE_ADDENDUM_MODAL_TITLE,
            props: {
              addendum,
              deleteAddendum: RequestService.deleteAddendum,
              refreshDocuments: () => {
                dispatch(fetchDocuments({ studyId: studyGid, participantId }));
              }
            }
          })
        );
      },
    showJournal: (consentGid, consentingGid) => {
      dispatch(changeTab(3));
      dispatch(publishConsentGidandConsentingGidForJournal(consentGid, consentingGid));
      dispatch(fetchConsentingJournal(consentingGid));
    },
    closePdf: () => {
      dispatch(closeDocumentPdf());
    },
    uploadPdf: (consentingGid, consenting, pdf) => {
      dispatch(uploadPdf(consentingGid, consenting, pdf));
    },
    showError: (error) => {
      dispatch(errorFetchingDocuments(error));
    },
    deleteDocument: (consenting, action) => {
      dispatch(
        showModal(MODAL_TYPE_DELETE_PDF, {
          shouldCloseOnOverlayClick: false,
          className: 'delete-document-modal',
          title: DocumentsService.getTitleByAction(action),
          props: { consenting, action }
        })
      );
    },
    showAddNewDocumentModal(participantJourneyGid, consentingGid) {
      dispatch(fetchConsenting(consentingGid));
      dispatch(fetchAvailableDocuments(participantJourneyGid));
      dispatch(
        showModal(MODAL_TYPE_ADD_NEW_PARTICIPANT_DOCUMENT, {
          shouldCloseOnOverlayClick: true,
          className: 'add-new-participant-document-modal',
          title: ADD_NEW_PARTICIPANT_DOCUMENT_TITLE
        })
      );
    }
  };
};

const CDocuments = connect(mapStateToProps, mapDispatchToProps)(Documents);

export default CDocuments;
